/**
 * Phone table reflow support (see mobile.css).
 *
 * On narrow screens, tables with four or more columns are rendered as
 * stacked rows and every cell shows its column header in front of the
 * value. CSS can do the layout but cannot read the matching <th> text, so
 * this module mirrors each header onto its body cells as `data-label`.
 * It runs for every Mantine table on the page (there are dozens, many
 * re-rendered live), driven by a single MutationObserver and coalesced to
 * one pass per animation frame, and only while the phone media query
 * matches. React ignores the extra attribute on re-render.
 */
const PHONE_QUERY = "(max-width: 48em)";
const STACK_MIN_COLUMNS = 4;

function normalize(text: string | null): string {
  return (text ?? "").replace(/\s+/g, " ").trim();
}

/** Per-column labels, merging multi-row headers ("ETH · Trades") while
 *  honouring colSpan / rowSpan the way the table grid does. */
function headerLabels(table: HTMLTableElement): string[] | null {
  if (!table.tHead) return null;
  const slots: string[][] = [];
  // Columns still covered by a rowSpan from an earlier header row.
  const pending: number[] = [];
  for (const row of Array.from(table.tHead.rows)) {
    let col = 0;
    for (const cell of Array.from(row.cells)) {
      while ((pending[col] ?? 0) > 0) col++;
      const text = normalize(cell.textContent);
      for (let i = 0; i < cell.colSpan; i++, col++) {
        const slot = (slots[col] ??= []);
        if (text && !slot.includes(text)) slot.push(text);
        pending[col] = cell.rowSpan;
      }
    }
    for (let i = 0; i < pending.length; i++) pending[i] = Math.max(0, (pending[i] ?? 0) - 1);
  }
  if (slots.length < STACK_MIN_COLUMNS) return null;
  return slots.map((parts) => parts.join(" · "));
}

function labelTable(table: HTMLTableElement): void {
  const labels = headerLabels(table);
  if (!labels) return;
  for (const body of Array.from(table.tBodies)) {
    for (const row of Array.from(body.rows)) {
      let col = 0;
      for (const cell of Array.from(row.cells)) {
        // Spanning cells (empty states, detail rows) have no single header.
        const label = cell.colSpan === 1 ? labels[col] ?? "" : "";
        if (label) {
          if (cell.dataset.label !== label) cell.dataset.label = label;
        } else if (cell.dataset.label !== undefined) {
          delete cell.dataset.label;
        }
        col += cell.colSpan;
      }
    }
  }
}

export function installMobileTableLabels(): void {
  if (typeof window === "undefined" || typeof MutationObserver === "undefined") return;
  const media = window.matchMedia(PHONE_QUERY);
  let scheduled = false;
  const run = () => {
    scheduled = false;
    if (!media.matches) return;
    document.querySelectorAll<HTMLTableElement>("table.mantine-Table-table").forEach(labelTable);
  };
  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(run);
  };
  new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true, characterData: true });
  media.addEventListener("change", schedule);
  schedule();
}
